import apiClient from './api';

// 知识库相关类型定义
export interface KnowledgeBase {
  id: number;
  name: string;
  description?: string;
  dataset_name: string;
  build_status: 'new' | 'building' | 'completed';
  is_enabled: boolean;
  document_count: number;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeDocument {
  id: number;
  knowledge_base_id: number;
  original_filename: string;
  file_path: string;
  file_size?: number;
  file_type?: string;
  conversion_status: 'pending' | 'processing' | 'completed' | 'failed';
  converted_path?: string;
  conversion_error?: string;
  cognee_status: 'pending' | 'processing' | 'completed' | 'failed';
  cognee_error?: string;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeTask {
  id: number;
  knowledge_base_id: number;
  document_id?: number;
  task_type: 'document_conversion' | 'dataset_reconstruction';
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  task_data?: any;
  result_data?: any;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface CreateKnowledgeBaseRequest {
  name: string;
  description?: string;
  is_enabled?: boolean;
}

export interface UpdateKnowledgeBaseRequest {
  name?: string;
  description?: string;
  is_enabled?: boolean;
  build_status?: string;
}

export interface KnowledgeBaseListResponse {
  success: boolean;
  message: string;
  data: KnowledgeBase[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface KnowledgeBaseDocumentsResponse {
  success: boolean;
  message: string;
  data: {
    documents: KnowledgeDocument[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  };
}

export interface KnowledgeBaseTasksResponse {
  success: boolean;
  message: string;
  data: {
    tasks: KnowledgeTask[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  };
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

// 知识库服务类
export class KnowledgeBaseService {
  // 获取知识库列表
  async getKnowledgeBases(params: {
    page?: number;
    page_size?: number;
    search?: string;
    build_status?: string;
    is_enabled?: boolean;
  } = {}): Promise<KnowledgeBaseListResponse> {
    const response = await apiClient.get('/knowledge-base/list', params);
    return response;
  }

  // 创建知识库
  async createKnowledgeBase(data: CreateKnowledgeBaseRequest): Promise<ApiResponse> {
    const response = await apiClient.post('/knowledge-base/create', data);
    return response;
  }

  // 获取知识库详情
  async getKnowledgeBase(knowledgeBaseId: number): Promise<ApiResponse> {
    const response = await apiClient.get(`/knowledge-base/${knowledgeBaseId}`);
    return response;
  }

  // 更新知识库
  async updateKnowledgeBase(
    knowledgeBaseId: number,
    data: UpdateKnowledgeBaseRequest
  ): Promise<ApiResponse> {
    const response = await apiClient.put(`/knowledge-base/${knowledgeBaseId}`, data);
    return response;
  }

  // 删除知识库
  async deleteKnowledgeBase(knowledgeBaseId: number): Promise<ApiResponse> {
    const response = await apiClient.delete(`/knowledge-base/${knowledgeBaseId}`);
    return response;
  }

  // 获取知识库文档列表
  async getKnowledgeBaseDocuments(
    knowledgeBaseId: number,
    params: {
      page?: number;
      page_size?: number;
      conversion_status?: string;
    } = {}
  ): Promise<KnowledgeBaseDocumentsResponse> {
    const response = await apiClient.get(`/knowledge-base/${knowledgeBaseId}/documents`, params);
    return response;
  }

  // 上传文档到知识库
  async uploadDocument(
    knowledgeBaseId: number,
    file: File,
    onUploadProgress?: (progress: number) => void
  ): Promise<ApiResponse> {
    const response = await apiClient.upload(
      `/knowledge-base/${knowledgeBaseId}/upload`,
      file,
      onUploadProgress
    );
    return response;
  }

  // 获取知识库任务列表
  async getKnowledgeBaseTasks(
    knowledgeBaseId: number,
    params: {
      page?: number;
      page_size?: number;
      task_type?: string;
      status?: string;
    } = {}
  ): Promise<KnowledgeBaseTasksResponse> {
    const response = await apiClient.get(`/knowledge-base/${knowledgeBaseId}/tasks`, params);
    return response;
  }

  // 创建手动任务
  async createManualTask(
    knowledgeBaseId: number,
    taskType: 'document_conversion' | 'dataset_reconstruction',
    documentId?: number
  ): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('task_type', taskType);
    if (documentId) {
      formData.append('document_id', documentId.toString());
    }

    const response = await apiClient.post(`/knowledge-base/${knowledgeBaseId}/tasks`, formData);
    return response;
  }

  // 重试任务
  async retryTask(taskId: number): Promise<ApiResponse> {
    const response = await apiClient.post(`/knowledge-base/tasks/${taskId}/retry`);
    return response;
  }

  // 获取系统状态
  async getSystemStatus(): Promise<ApiResponse> {
    const response = await apiClient.get('/knowledge-base/system/status');
    return response;
  }
}

// 创建服务实例
export const knowledgeBaseService = new KnowledgeBaseService();
export default knowledgeBaseService;